/** What a verification recorded, on the Recorded panel - Q-07, G-Wadmin-12.
 *
 * The panel used to say how many calculations and ledger entries were written, and explained dual
 * reporting in a sentence: no quantity, no figure and no receipt, so "its receipt shows the document"
 * could not be checked from the place the person had just pressed Verify. It now shows the quantity
 * the record holds and one figure per basis, each with the receipt that makes it a figure (rule 3).
 *
 * **Read from the record, never assembled here.** The quantity and the calculations are
 * `GET /activities/{id}`, the same answer the record page gives, so this panel and the receipt cannot
 * disagree. Tonnes are rounded once, from the exact kilograms the server stores (rule 5), and the
 * stored scale is not printed. **The two bases are two figures and are never added** (Scope 2
 * Guidance); location-based comes first, as it does on the record page.
 *
 * A read that fails says so with a retry. It does not leave the panel blank, which would read as a
 * verification that produced nothing.
 */

import { useId } from "react";

import type { Activity } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { Skeleton } from "../components/ui";
import { statedQuantity, tonnes, unitLabel } from "../lib/format";
import styles from "./Evidence.module.css";

type Calc = Activity["calculations"][number];

/** A figure's basis as a person reads it. `null` is a figure with one basis (a fuel, a refrigerant). */
const BASIS: Record<string, string> = { location: "Location-based", market: "Market-based" };

function ordered(calculations: readonly Calc[]): Calc[] {
  const rank = (calc: Calc) => (calc.method === "market" ? 1 : 0);
  return [...calculations].sort((a, b) => rank(a) - rank(b));
}

export interface RecordedRead {
  data: Activity | null | undefined;
  error: unknown;
  isPending: boolean;
  refetch: () => unknown;
}

export function RecordedFigures({
  read,
  onReceipt,
}: {
  read: RecordedRead;
  onReceipt: (calculationId: string) => void;
}) {
  const headingId = useId();
  if (read.error) {
    return (
      <div data-testid="recorded-failed">
        <ReadFailed
          of="The recorded figure"
          error={read.error}
          onRetry={() => void read.refetch()}
        />
      </div>
    );
  }
  if (read.isPending) return <Skeleton />;
  const activity = read.data;
  // A 404 ("not a record you can see") is not a failure to retry: the way on below still works.
  if (!activity) return null;
  return (
    <dl className={styles.recorded} aria-labelledby={headingId} data-testid="recorded-figures">
      <div>
        <dt id={headingId}>Recorded</dt>
        <dd className="num" data-testid="recorded-quantity">
          {`${statedQuantity(activity.quantity)} ${unitLabel(activity.unit)}`}
        </dd>
      </div>
      {ordered(activity.calculations).map((calc) => (
        <div
          key={calc.calculation_id}
          data-testid={`recorded-figure-${calc.method ?? "single"}`}
          data-basis={calc.method ?? "single"}
        >
          <dt>{BASIS[calc.method ?? ""] ?? "Emissions"}</dt>
          <dd>
            <span className="num">{tonnes(calc.kgco2e, 2)}</span> tCO₂e{" "}
            <button
              type="button"
              className={styles.fixLink}
              data-testid={`recorded-receipt-${calc.calculation_id}`}
              aria-label={`Receipt for the ${BASIS[calc.method ?? ""]?.toLowerCase() ?? "emissions"} figure`}
              onClick={() => onReceipt(calc.calculation_id)}
            >
              Receipt
            </button>
          </dd>
        </div>
      ))}
    </dl>
  );
}
